import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Copy, Check, Pencil, Save, X } from 'lucide-react';

const FIELD_GROUPS = [
  {
    title: 'Tax & Compliance',
    fields: [
      { key: 'pan', label: 'PAN' },
      { key: 'gst', label: 'GSTIN' },
      { key: 'cin', label: 'CIN' },
      { key: 'tan', label: 'TAN' },
      { key: 'msme', label: 'MSME / Udyam' },
    ],
  },
  {
    title: 'Banking',
    fields: [
      { key: 'bank_name', label: 'Bank Name' },
      { key: 'bank_branch', label: 'Branch' },
      { key: 'bank_account_number', label: 'Account No.' },
      { key: 'bank_ifsc', label: 'IFSC' },
    ],
  },
  {
    title: 'Address',
    fields: [
      { key: 'registered_address', label: 'Registered Address', multiline: true },
      { key: 'communication_address', label: 'Communication Address', multiline: true },
    ],
  },
  {
    title: 'Authorized Signatory',
    fields: [
      { key: 'authorized_signatory', label: 'Name' },
      { key: 'signatory_designation', label: 'Designation' },
      { key: 'signatory_email', label: 'Email' },
    ],
  },
];

function CopyableField({ label, value, multiline = false }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    if (!value) return;
    navigator.clipboard.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="text-[11px] uppercase tracking-wide flex-shrink-0" style={{ color: '#6B7280', minWidth: 130 }}>{label}</span>
      <span className={`flex-1 text-sm font-mono ${multiline ? 'whitespace-pre-line' : 'truncate'}`} style={{ color: '#1B2A4A' }}>
        {value || <em className="text-gray-400 not-italic">—</em>}
      </span>
      {value && (
        <button
          onClick={handle}
          className="flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
          title={copied ? 'Copied!' : 'Copy to clipboard'}
          data-testid={`copy-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
          {copied ? <Check className="h-3.5 w-3.5" style={{ color: '#2D8B5E' }} /> : <Copy className="h-3.5 w-3.5" style={{ color: '#3B5BA9' }} />}
        </button>
      )}
    </div>
  );
}

export default function LegalComplianceTab({ divisionId }) {
  const { user } = useAuth();
  const isCEO = user?.role === 'ceo';
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/legal-entities/by-division/${divisionId}`);
      setEntity(r.data);
      setDraft(r.data || {});
    } catch {
      setEntity(null);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [divisionId]);

  const startEdit = () => {
    setDraft(entity || {});
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(entity || {});
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!entity?.id) return;
    setSaving(true);
    try {
      const payload = {};
      FIELD_GROUPS.forEach((g) => g.fields.forEach((f) => {
        payload[f.key] = draft[f.key] ?? '';
      }));
      const r = await api.patch(`/legal-entities/${entity.id}`, payload);
      setEntity(r.data);
      setEditing(false);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return <div className="text-sm text-gray-500">Loading legal entity…</div>;
  if (!entity) {
    return (
      <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E1DC' }}>
        <p className="text-sm" style={{ color: '#6B7280' }}>
          No legal entity is mapped to this division yet.{' '}
          {isCEO && (
            <span>Run <code>backend/scripts/seed_legal_entities.py</code> or create one via the API.</span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="legal-compliance-tab">
      <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold" style={{ color: '#1B2A4A', fontFamily: 'Georgia, serif' }}>
              {entity.name}
            </h2>
            <p className="text-xs" style={{ color: '#6B7280' }}>
              Legal entity · {(entity.division_ids || []).join(' · ')}
            </p>
          </div>
          {isCEO && !editing && (
            <button
              onClick={startEdit}
              className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold"
              style={{ backgroundColor: '#3B5BA9', color: 'white' }}
              data-testid="legal-entity-edit-btn"
            >
              <Pencil className="h-3 w-3" /> Edit
            </button>
          )}
          {isCEO && editing && (
            <div className="flex gap-2">
              <button
                onClick={saveEdit}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-60"
                style={{ backgroundColor: '#2D8B5E', color: 'white' }}
                data-testid="legal-entity-save-btn"
              >
                <Save className="h-3 w-3" /> {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={cancelEdit}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold"
                style={{ backgroundColor: '#F3F4F6', color: '#1B2A4A' }}
                data-testid="legal-entity-cancel-btn"
              >
                <X className="h-3 w-3" /> Cancel
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          {FIELD_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="text-[11px] uppercase tracking-wide font-semibold mb-2" style={{ color: '#3B5BA9' }}>
                {group.title}
              </h3>
              <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {group.fields.map((f) => (
                  editing ? (
                    <div key={f.key} className="py-1.5 flex items-start gap-2">
                      <span className="text-[11px] uppercase tracking-wide flex-shrink-0" style={{ color: '#6B7280', minWidth: 130 }}>{f.label}</span>
                      {f.multiline ? (
                        <textarea
                          value={draft[f.key] || ''}
                          onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                          className="flex-1 border rounded px-2 py-1 text-sm font-mono"
                          style={{ borderColor: '#E5E1DC', minHeight: 60 }}
                          data-testid={`legal-input-${f.key}`}
                        />
                      ) : (
                        <input
                          value={draft[f.key] || ''}
                          onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                          className="flex-1 border rounded px-2 py-1 text-sm font-mono"
                          style={{ borderColor: '#E5E1DC' }}
                          data-testid={`legal-input-${f.key}`}
                        />
                      )}
                    </div>
                  ) : (
                    <CopyableField key={f.key} label={f.label} value={entity[f.key]} multiline={f.multiline} />
                  )
                ))}
              </div>
            </div>
          ))}
        </div>

        {entity.notes && !editing && (
          <div className="mt-4 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
            <p className="text-[11px] uppercase tracking-wide font-semibold mb-1" style={{ color: '#3B5BA9' }}>Notes</p>
            <p className="text-sm" style={{ color: '#374151' }}>{entity.notes}</p>
          </div>
        )}
      </div>

      <div className="text-[11px] italic" style={{ color: '#9CA3AF' }}>
        Tap any field's copy icon to instantly paste PAN/GST/CIN into proposals & invoices.
      </div>
    </div>
  );
}
